import {
  useGlobalNotifier,
  useMessage,
  useSpreadsheetState,
} from "@vrf/fr-vrf-core-react";
import { FrVrfSpreadsheet } from "@vrf/fr-vrf-spreadsheet-react";
import {
  FrVrfButton,
  FrVrfButtonPanel,
  FrVrfCheckboxField,
  FrVrfDropdownField,
  FrVrfFooter,
  FrVrfFormPanel,
  FrVrfPanel,
  FrVrfSingleLineDisplayField,
  FrVrfSingleLineInputField,
} from "@vrf/fr-vrf-web-components-react";
import React, { useEffect, useMemo } from "react";
import {
  RootCpntIchrKeys,
  RootCpntIchrSpreadsheetRow,
  SpreadsheetState,
  rootComponentEditDialogInitialState,
} from ".";
import { ApplicationMessageScreen } from "../../../messages/screen-message";
import { usePageNavigation } from "../../../pages/use-page-navigation";
import { RootCpntIchr } from "../../../services/component/get-component/GetComponentResponse";
import { useUpdateRootComponent } from "../../../services/root-component";
import { UpdateRootComponentRequest } from "../../../services/root-component/update-root-component/UpdateRootComponentRequest";
import { mappingErrorResponseToSpreadsheet } from "../../../util";
import { InDefCommonFlagType } from "../../../util/general-code/enums/general-codes";
import { createModal } from "../../modal";
import {
  TEMPLATE_FILE_URL,
  isChecked,
} from "./RootComponentEditDialogConstant";
import { useRootComponentEditDialogState } from "./useRootComponentEditDialogState";
import { Cell } from "@vrf/fr-vrf-spreadsheet-core-templating";
import _ from "lodash";

export const RootComponentEditDialogContent: React.FC = () => {
  const { showConfirmationDialog, errorMessages, showToast, resetUserError } =
    useGlobalNotifier();
  const { messageForScreen } = useMessage();
  const { moveToModalPage, closeModalPage, isModalPage } = usePageNavigation();

  const [dialogState, setDialogState] = useRootComponentEditDialogState();
  const { rootComponent, dataList } = dialogState;

  const [updateRootComponentResponse, sendUpdateRootComponentRequest] =
    useUpdateRootComponent();

  const [spreadsheetState, setSpreadsheetState, initSpreadsheetState] =
    useSpreadsheetState<SpreadsheetState>();

  const spreadsheetInitialData = useMemo<SpreadsheetState>(() => {
    const rootCpntIchr = rootComponent.rootCpntIchrList.map((e) => ({
      ...e,
      messageId: "",
      message: "",
      column: "",
      errorColumnList: [],
      errorMessage: "",
      messageList: [],
    }));
    return {
      rootCpntIchr: rootCpntIchr,
    };
  }, [rootComponent.rootCpntIchrList]);

  const onInitialDisplayed = () => {
    moveToModalPage();
    resetUserError();
  };
  useEffect(onInitialDisplayed, []);

  const closeModal = () => {
    closeModalPage();
    setDialogState((prevState) => ({
      ...prevState,
      isShow: false,
      isEdit: false,
      formValidation: {
        rootCpntName: true,
        ownTypeCd: true,
        systemName: true,
        systemId: true,
        subSystemId: true,
        notSysUsg: true,
        alctnTypeCd: true,
        clodSrvcTypeCd: true,
        rgnTypeCd: true,
        acntName: true,
      },
    }));
  };

  const onUpdateRootComponent = () => {
    if (!updateRootComponentResponse) return;

    if (Array.isArray(updateRootComponentResponse)) {
      const currentSpreadsheetRecordData: RootCpntIchrSpreadsheetRow[] =
        spreadsheetState ? [...spreadsheetState.rootCpntIchr] : [];

      const newSpreadsheetRecordData = mappingErrorResponseToSpreadsheet(
        updateRootComponentResponse,
        currentSpreadsheetRecordData,
        dialogState.displayColumn,
      );

      setSpreadsheetState({
        ...spreadsheetState,
        rootCpntIchr: newSpreadsheetRecordData,
      });
    } else {
      showToast({
        design: "completion",
        message: messageForScreen(ApplicationMessageScreen.APP_SCN_00044),
      });
      closeModalPage();
      setDialogState((_prevState) => ({
        ...rootComponentEditDialogInitialState,
        isUpdated: true,
      }));
    }
  };
  useEffect(onUpdateRootComponent, [updateRootComponentResponse]);

  const onClickCancel = () => {
    if (dialogState.isEdit) {
      showConfirmationDialog({
        title: "",
        message: messageForScreen(ApplicationMessageScreen.APP_SCN_00033),
        type: "confirm",
        onOkButtonClicked: closeModal,
      });
      return;
    }
    closeModal();
  };

  /**
   * Root Component
   */
  const onChangeValueHandle = (name: string) => (event: CustomEvent) => {
    const value = event.detail.value;
    const isValid = event.detail.isValid;
    setDialogState((prevState) => ({
      ...prevState,
      isEdit: true,
      rootComponent: {
        ...prevState.rootComponent,
        [name]: value,
      },
      formValidation: {
        ...prevState.formValidation,
        [name]: isValid,
      },
    }));
  };

  const onChnageCheckHandle = (name: string) => (event: CustomEvent) => {
    const value = event.detail.value;
    const isValid = event.detail.isValid;
    setDialogState((prevState) => ({
      ...prevState,
      isEdit: true,
      rootComponent: {
        ...prevState.rootComponent,
        [name]: value ? InDefCommonFlagType.YES : InDefCommonFlagType.NO,
      },
      formValidation: {
        ...prevState.formValidation,
        [name]: isValid,
      },
    }));
  };

  /**
   * Add Row Button Clicked.
   * @param event that is passed from spreadsheet.
   */
  const onAddRowClicked = () => {
    if (!spreadsheetState) return;
    const insertRowIndex: number =
      (dialogState.spreadsheetSelectedRowIndex ?? 0) + 1;

    const newSpreadsheetState: RootCpntIchrSpreadsheetRow[] = spreadsheetState
      ? [...spreadsheetState.rootCpntIchr]
      : [];
    const addSpreadsheetRow: RootCpntIchrSpreadsheetRow = {
      ichrId: "",
      ichrName: "",
      pstnName: "",
      cmpnyName: "",
      telNum: "",
      mlAdrs: "",
      updateDateTimeMRootCpntIchr: "",
      messageId: "",
      message: "",
      column: "",
      errorColumnList: [],
      errorMessage: "",
      messageList: [],
    };
    newSpreadsheetState.splice(insertRowIndex, 0, addSpreadsheetRow);

    setSpreadsheetState({
      ...spreadsheetState,
      rootCpntIchr: [...newSpreadsheetState],
    });
  };

  /**
   * Spreadsheet cell selected.
   * @param event that is passed from spreadsheet.
   */
  const onCellSelected = (event: CustomEvent) => {
    const rowIndex: number = event.detail.cell?.data.index;
    setDialogState((prevState) => ({
      ...prevState,
      spreadsheetSelectedRowIndex: rowIndex,
    }));
  };

  /**
   * Spreadsheet delete icon clicked.
   * @param event that is passed from spreadsheet.
   */
  const onDeleteIconClicked = (event: CustomEvent) => {
    if (event.detail.name !== "deleteIcon") return;
    const rowIndex: number = event.detail.cell.data.index;
    const newSpreadsheetState: RootCpntIchrSpreadsheetRow[] = spreadsheetState
      ? [...spreadsheetState.rootCpntIchr]
      : [];
    newSpreadsheetState.splice(rowIndex, 1);
    setSpreadsheetState({
      ...spreadsheetState,
      rootCpntIchr: newSpreadsheetState,
    });
  };

  const isRootCpntIchrKey = (arg: any): arg is keyof RootCpntIchr => {
    const dtKindKeys: (keyof RootCpntIchr)[] = [
      "ichrName",
      "pstnName",
      "cmpnyName",
      "telNum",
      "mlAdrs",
    ];
    return dtKindKeys.includes(arg);
  };

  const onEditSpreadsheet = (event: CustomEvent) => {
    if (!spreadsheetState) return;
    const eventName: string = event.detail.name;
    const rowIndex: number = event.detail.cell.data.index;
    const value = event.detail.cell.value;

    if (isRootCpntIchrKey(eventName)) {
      const copyRootCpntIchrList: RootCpntIchrSpreadsheetRow[] = JSON.parse(
        JSON.stringify(spreadsheetState.rootCpntIchr),
      );

      copyRootCpntIchrList[rowIndex][eventName] = value;

      const newState = {
        ...spreadsheetState,
        rootCpntIchr: copyRootCpntIchrList,
      };
      setSpreadsheetState(newState);
      setDialogState((prevState) => ({ ...prevState, isEdit: true }));
    }
  };

  const onCellBulkEdited = (event: CustomEvent) => {
    if (!spreadsheetState) return;
    const newPicAddressList = _.cloneDeep(
      spreadsheetState?.rootCpntIchr,
    ) as RootCpntIchrSpreadsheetRow[];

    const colName: RootCpntIchrKeys = event.detail.name;
    event.detail.affectedCells.cells.forEach((item: Cell) => {
      const value: string = item.value;
      const rowIdx: number = item.data.index;

      newPicAddressList[rowIdx][colName] = value;
    });

    setSpreadsheetState({
      ...spreadsheetState,
      rootCpntIchr: newPicAddressList,
    });
    setDialogState((prevState) => ({ ...prevState, isEdit: true }));
  };

  /**
   * Footer
   */
  const onClickSaveButton = () => {
    showConfirmationDialog({
      title: "",
      message: messageForScreen(ApplicationMessageScreen.APP_SCN_00045),
      type: "confirm",
      onOkButtonClicked: () => {
        resetUserError();
        const updateRootIchrList =
          spreadsheetState?.rootCpntIchr.map((e, idx) => ({
            index: idx,
            ichrName: e.ichrName,
            pstnName: e.pstnName,
            cmpnyName: e.cmpnyName,
            telNum: e.telNum,
            mlAdrs: e.mlAdrs,
          })) ?? [];

        const updateRequestDate: UpdateRootComponentRequest = {
          dipByRgn: rootComponent.dipByRgn,
          rootCpntId: rootComponent.rootCpntId,
          rootCpntName: rootComponent.rootCpntName,
          ownTypeCd: rootComponent.ownTypeCd,
          systemName: rootComponent.systemName,
          systemId: rootComponent.systemId,
          subSystemId: rootComponent.subSystemId,
          alctnTypeCd: rootComponent.alctnTypeCd,
          clodSrvcTypeCd: rootComponent.clodSrvcTypeCd,
          rgnTypeCd: rootComponent.rgnTypeCd,
          acntName: rootComponent.acntName,
          notSysUsg:
            rootComponent.notSysUsg === InDefCommonFlagType.YES
              ? rootComponent.notSysUsg
              : InDefCommonFlagType.NO,
          updateDateTime: rootComponent.updateDateTimeMRootCpnt,
          rootCpntIchrList: updateRootIchrList,
        };
        sendUpdateRootComponentRequest(updateRequestDate);
      },
    });
  };

  return createModal(
    "Root Component Edit Dialog",
    <div>
      <div
        style={{
          paddingTop: "10px",
          paddingBottom: "30px",
          marginLeft: "30px",
        }}
      >
        {/* Root Component */}
        <FrVrfPanel panelTitle="Root component">
          <FrVrfFormPanel columns={2}>
            <FrVrfFormPanel>
              <FrVrfSingleLineDisplayField
                label="Root Component ID"
                firstValue={rootComponent.rootCpntId}
              />
              <FrVrfSingleLineInputField
                labelContent="Root Component Name"
                content={rootComponent.rootCpntName}
                onInputValueChanged={onChangeValueHandle("rootCpntName")}
                required
                constraint="[Size(value, {min:0 , max: 256}), IsNotEmpty(value)]"
                errorMessage={errorMessages(
                  dialogState.fieldPath.rootCpntName,
                  isModalPage(),
                )}
              />
              <FrVrfSingleLineDisplayField
                label="DIP By Region"
                firstValue={rootComponent.dipByRgn}
              />
              <FrVrfDropdownField
                autocomplete={false}
                labelContent="Owner Type"
                selectedValue={rootComponent.ownTypeCd}
                includeDefaultValue={false}
                items={dataList.cpntOwnType}
                onValueChanged={onChangeValueHandle("ownTypeCd")}
                errorMessage={errorMessages(
                  dialogState.fieldPath.ownTypeCd,
                  isModalPage(),
                )}
              />
              <FrVrfSingleLineInputField
                labelContent="System Name"
                content={rootComponent.systemName}
                onInputValueChanged={onChangeValueHandle("systemName")}
                errorMessage={errorMessages(
                  dialogState.fieldPath.systemName,
                  isModalPage(),
                )}
                constraint="[Size(value, {min:0 , max: 256})]"
              />
              <FrVrfSingleLineInputField
                labelContent="System ID"
                content={rootComponent.systemId}
                onInputValueChanged={onChangeValueHandle("systemId")}
                errorMessage={errorMessages(
                  dialogState.fieldPath.systemId,
                  isModalPage(),
                )}
                constraint="[Size(value, {min:0 , max: 256})]"
              />
              <FrVrfSingleLineInputField
                labelContent="Sub System ID"
                content={rootComponent.subSystemId}
                onInputValueChanged={onChangeValueHandle("subSystemId")}
                errorMessage={errorMessages(
                  dialogState.fieldPath.subSystemId,
                  isModalPage(),
                )}
                constraint="[Size(value, {min:0 , max: 256})]"
              />
              <FrVrfCheckboxField
                labelContent="Not System Usage"
                checked={isChecked(rootComponent.notSysUsg)}
                onCheckedChanged={onChnageCheckHandle("notSysUsg")}
                errorMessage={errorMessages(
                  dialogState.fieldPath.notSysUsg,
                  isModalPage(),
                )}
              />
            </FrVrfFormPanel>
            <FrVrfFormPanel>
              <FrVrfDropdownField
                autocomplete={false}
                labelContent="Component Allocation Type"
                selectedValue={rootComponent.alctnTypeCd}
                includeDefaultValue={false}
                items={dataList.cpntAlctnType}
                onValueChanged={onChangeValueHandle("alctnTypeCd")}
                errorMessage={errorMessages(
                  dialogState.fieldPath.alctnTypeCd,
                  isModalPage(),
                )}
              />
              <FrVrfDropdownField
                autocomplete={false}
                labelContent="Target Cloud Services"
                selectedValue={rootComponent.clodSrvcTypeCd}
                includeDefaultValue={false}
                items={dataList.clodSrvc}
                onValueChanged={onChangeValueHandle("clodSrvcTypeCd")}
                errorMessage={errorMessages(
                  dialogState.fieldPath.clodSrvcTypeCd,
                )}
              />
              <FrVrfDropdownField
                autocomplete={false}
                labelContent="Target Cloud Region"
                selectedValue={rootComponent.rgnTypeCd}
                includeDefaultValue={false}
                items={dataList.clodSrvcRgn}
                onValueChanged={onChangeValueHandle("rgnTypeCd")}
                errorMessage={errorMessages(
                  dialogState.fieldPath.rgnTypeCd,
                  isModalPage(),
                )}
              />
              <FrVrfSingleLineInputField
                labelContent="Target Cloud Account"
                content={rootComponent.acntName}
                onInputValueChanged={onChangeValueHandle("acntName")}
                errorMessage={errorMessages(
                  dialogState.fieldPath.acntName,
                  isModalPage(),
                )}
                constraint="[Size(value, {min:0 , max: 256})]"
              />
              <FrVrfPanel panelTitle="Pic Address">
                <FrVrfButtonPanel>
                  <FrVrfButton
                    content="Add Row"
                    size="small"
                    theme="secondary"
                    slot="left"
                    onClicked={onAddRowClicked}
                    authActId="root_component_edit_dialog.button.add_row"
                  />
                </FrVrfButtonPanel>
                <br />
                <FrVrfSpreadsheet
                  data={spreadsheetInitialData}
                  templateUrl={TEMPLATE_FILE_URL}
                  onInitialized={initSpreadsheetState}
                  onCellSelected={onCellSelected}
                  onCellClicked={onDeleteIconClicked}
                  onCellEdited={onEditSpreadsheet}
                  onCellBulkEdited={onCellBulkEdited}
                  maxHeight={150}
                />
              </FrVrfPanel>
            </FrVrfFormPanel>
          </FrVrfFormPanel>
        </FrVrfPanel>
        {/* Footer */}
        <FrVrfFooter>
          <FrVrfButton
            content="Cancel"
            theme="secondary"
            size="large"
            onClicked={onClickCancel}
          />
          <FrVrfButton
            content="Save"
            theme="primary"
            size="large"
            onClicked={onClickSaveButton}
            authActId="root_component_edit_dialog.button.save"
            disabled={Object.values(dialogState.formValidation).some(
              (isValid) => isValid === false,
            )}
          />
        </FrVrfFooter>
      </div>
    </div>,
    onClickCancel,
    "large",
  );
};
